// src/utils/pushNotificationManager.js

// Función estándar para convertir la llave pública VAPID (Base64) en un Array Buffer
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

/**
 * Verifica si las notificaciones Web Push son soportadas por el navegador actual
 * @returns {boolean}
 */
export const isPushNotificationsSupported = () => {
    return 'serviceWorker' in navigator && 'PushManager' in window;
};

/**
 * Solicita permisos de notificación e inicia el registro del dispositivo para Web Push
 * 
 * @returns {Promise<boolean>} - Retorna true si se suscribió con éxito, false en caso contrario
 */
export const subscribeUserToPush = async () => {
    if (!isPushNotificationsSupported()) {
        console.warn('⚠️ Las notificaciones Web Push no son soportadas en este dispositivo/navegador');
        return false;
    }

    try {
        const clientToken = localStorage.getItem('clientToken');
        if (!clientToken) {
            console.warn('⚠️ No hay sesión de cliente activa para registrar notificaciones');
            return false;
        }

        // 1. Obtener la llave pública VAPID del servidor
        const keyResponse = await fetch('/api/client/notifications/vapid-public-key', {
            headers: {
                'Authorization': `Bearer ${clientToken}`
            }
        });

        if (!keyResponse.ok) {
            throw new Error('No se pudo obtener la llave pública VAPID desde el backend');
        }

        const { publicKey: vapidPublicKey } = await keyResponse.json();

        // 2. Solicitar permisos de notificación
        const permission = await Notification.requestPermission();
        console.log(`[Push] Permiso de notificaciones: ${permission}`);
        
        if (permission !== 'granted') {
            console.log('[Push] Permiso denegado por el usuario');
            return false;
        }

        // 3. Obtener el registro del Service Worker activo
        const registration = await navigator.serviceWorker.ready;

        // 4. Suscribir al usuario al servicio Web Push del navegador
        let subscription = await registration.pushManager.getSubscription();

        if (!subscription) {
            console.log('[Push] Creando nueva suscripción Web Push...');
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
            });
        }

        console.log('[Push] Suscripción obtenida con éxito, enviando al backend...', subscription);

        // 5. Enviar la suscripción al backend
        const subscribeResponse = await fetch('/api/client/notifications/subscribe', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${clientToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ subscription })
        });

        if (!subscribeResponse.ok) {
            throw new Error('Error al registrar la suscripción en el servidor');
        }

        console.log('✅ Suscripción Web Push registrada y guardada exitosamente en el servidor.');
        return true;
    } catch (error) {
        console.error('❌ Error registrando suscripción push:', error);
        return false;
    }
};

/**
 * Des-registra la suscripción Web Push del cliente
 */
export const unsubscribeUserFromPush = async () => {
    if (!isPushNotificationsSupported()) return false;

    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (subscription) {
            const clientToken = localStorage.getItem('clientToken');
            
            // Eliminar en el backend primero si hay sesión activa
            if (clientToken) {
                await fetch('/api/client/notifications/unsubscribe', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${clientToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ endpoint: subscription.endpoint })
                }).catch(err => console.error('Error al remover suscripción del servidor:', err));
            }

            // Des-registrar en el navegador
            await subscription.unsubscribe();
            console.log('✅ Notificaciones Web Push desactivadas localmente.');
            return true;
        }
    } catch (error) {
        console.error('Error des-registrando notificaciones push:', error);
    }
    return false;
};
